import { useEffect, useRef, useState } from 'react'
import { useAppStore } from './store/app'
import { bridge } from './bridge'
import { submitProSubscription } from './api/client'
import { sendListMyProSubscriptions, fetchTempAgentDefaults, startWS, setActiveChatRoom } from './api/ws'
import { activeChatFor } from './nav/activeChat'
import { RegistrationScreen } from './screens/registration/RegistrationScreen'
import { HomeScreen } from './screens/HomeScreen'
import { HouseholdScreen } from './screens/household/HouseholdScreen'
import { AccountScreen } from './screens/account/AccountScreen'
import { OrphanedScreen } from './screens/orphaned/OrphanedScreen'
import { OutdatedOverlay } from './screens/outdated/OutdatedOverlay'
import { PermissionOverlay } from './screens/permission/PermissionOverlay'
import { AnnouncementOverlay } from './screens/announcements/AnnouncementOverlay'
import { SheetStack } from './screens/SheetStack'
import { NavStack } from './screens/NavStack'
import { installBackHistory } from './nav/backHistory'
import { Toasts } from './screens/Toasts'
import { InitiatorNotices } from './screens/InitiatorNotices'
import { BottomTabBar, type Tab } from './screens/BottomTabBar'
import './screens/BottomTabBar.css'
import './App.css'

export function App() {
  const [activeTab, setActiveTab] = useState<Tab>('home')
  const isRegistered = useAppStore((s) => s.isRegistered)
  // Orphaned: signed in but with zero org memberships. Lands here
  // after the last household they belonged to was deleted or left. The
  // hasLoadedOrganizations guard prevents flashing OrphanedScreen
  // during the brief window before the first my_organizations
  // frame arrives.
  const isOrphaned = useAppStore(
    (s) =>
      s.isRegistered &&
      s.hasLoadedOrganizations &&
      s.availableOrganizations.length === 0,
  )
  // A device booted from the keychain with valid creds but no cached
  // current org (a deferred invitee, or a freshly-orphaned user) is
  // registered, but we don't yet know whether it belongs anywhere. Hold
  // on a neutral screen until organizations_list lands rather than
  // flashing HomeScreen scoped to an empty org. Normal returning users
  // have a cached org, so this never trips for them.
  const isResolvingOrg = useAppStore(
    (s) =>
      s.isRegistered &&
      !s.organizationID &&
      !s.hasLoadedOrganizations,
  )

  // Open the WebSocket as soon as the device is registered, independent
  // of which screen renders. This used to live in HomeScreen's mount,
  // which happened to run because a registered-but-org-less device briefly
  // rendered HomeScreen before organizations_list arrived. The moment we
  // hold such a device on a neutral screen instead (isResolvingOrg), that
  // incidental mount is gone — so the connection can't depend on it. An
  // orphaned invitee needs the socket precisely to discover they have no
  // household yet. connect() dedupes, so HomeScreen's own startWS is now a
  // harmless no-op.
  useEffect(() => {
    if (isRegistered) startWS()
  }, [isRegistered])

  // System back (Android's back gesture, the browser back button) steps back
  // one level in the app instead of leaving it. See nav/backHistory.ts.
  useEffect(() => installBackHistory(), [])

  // Tell the relay which room/thread is on screen, so it skips pushes for what
  // you're already looking at (nav/activeChat.ts). Re-sent on every reconnect:
  // the relay forgets a device's open chat when it disconnects, which is also
  // how pushes resume once the app is backgrounded.
  // Select plain strings: a selector returning a fresh object every call would
  // re-render forever.
  const activeRoomID = useAppStore((s) => activeChatFor(s.navStack[s.navStack.length - 1])?.roomID ?? null)
  const activeThreadRootID = useAppStore((s) => activeChatFor(s.navStack[s.navStack.length - 1])?.threadRootID)
  const chatConnectionStatus = useAppStore((s) => s.connectionStatus)
  useEffect(() => {
    if (chatConnectionStatus !== 'connected') return
    setActiveChatRoom(activeRoomID, activeThreadRootID)
  }, [chatConnectionStatus, activeRoomID, activeThreadRootID])

  const connectionStatus = useAppStore((s) => s.connectionStatus)
  const isSubscribed = useAppStore((s) => s.isSubscribed)
  const invitationsLinkPending = useAppStore((s) => s.invitationsLinkPending)
  const setInvitationsLinkPending = useAppStore((s) => s.setInvitationsLinkPending)
  const pendingPairCode = useAppStore((s) => s.pendingPairCode)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const organizationID = useAppStore((s) => s.organizationID) ?? ''
  const availableOrganizations = useAppStore((s) => s.availableOrganizations)
  const isOrgPro = availableOrganizations.find((o) => o.is_current)?.is_pro ?? false

  // When we (re)connect and the org still isn't Pro but there's a local
  // Apple transaction, silently claim it. Handles reinstall, restore on a
  // new device, and the case where the user subscribed while offline.
  // Gated on connectionStatus so organizations_list (and thus is_pro) is
  // already current before we decide whether to claim.
  const autoClaimAttempted = useRef(false)
  useEffect(() => {
    if (connectionStatus !== 'connected') {
      autoClaimAttempted.current = false  // reset on disconnect so next connect retries
      return
    }
    if (!bridge.isNative || !isSubscribed || isOrgPro || !organizationID) return
    if (autoClaimAttempted.current) return
    autoClaimAttempted.current = true

    bridge
      .call('storekit.latestTransactionJWS')
      .then(({ jws, productId }) => {
        if (!jws) return
        return submitProSubscription({
          signedTransaction: jws,
          organizationID,
          platform: bridge.isAndroid ? 'google' : 'apple',
          productId,
        })
      })
      .catch((err) => {
        console.warn('[hearth] auto-claim subscription failed:', err)
      })
  }, [connectionStatus, isSubscribed, isOrgPro, organizationID])

  // Invitation deep link for a signed-in user: just open the sheet. It
  // lists whatever this account was invited to, so there is nothing to
  // resolve first and nothing that can be "for the wrong account" —
  // which is why the old peek round-trip and the whole invite-mismatch
  // screen are gone. OrphanedScreen handles this state for itself.
  useEffect(() => {
    if (!invitationsLinkPending) return
    if (!isRegistered || isOrphaned) return
    setInvitationsLinkPending(false)
    pushSheet({ kind: 'invitations' })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationsLinkPending, isRegistered, isOrphaned])

  // Display pairing deep link (scanned kiosk QR, /pair?code=…): open the Devices
  // sheet, which jumps to Displays and pre-fills the code. The user confirms the
  // household + whether it's temporary and taps Pair — no silent claim. Pushed once
  // (the ref resets when DevicesSheet consumes the pending code).
  const pairSheetPushed = useRef(false)
  useEffect(() => {
    if (!pendingPairCode) {
      pairSheetPushed.current = false
      return
    }
    if (!isRegistered || isOrphaned || pairSheetPushed.current) return
    pairSheetPushed.current = true
    pushSheet({ kind: 'devices_debug' })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPairCode, isRegistered, isOrphaned])

  // Fetch the caller's own active subscription tier so AccountScreen and
  // PaywallSheet know whether to show an upgrade path. Runs on every
  // connect, org switch, and offer redemption so the UI stays accurate.
  // Resets to null on disconnect so stale data isn't shown.
  const hasActiveOffer = useAppStore((s) => s.hasActiveOffer)
  const setSubscribedProductID = useAppStore((s) => s.setSubscribedProductID)
  const setSubscribedPlatform = useAppStore((s) => s.setSubscribedPlatform)
  const setSubscribedExpiresAt = useAppStore((s) => s.setSubscribedExpiresAt)
  useEffect(() => {
    if (connectionStatus !== 'connected' || !isRegistered) {
      setSubscribedProductID(null)
      setSubscribedPlatform(null)
      setSubscribedExpiresAt(null)
      return
    }
    sendListMyProSubscriptions()
      .then((r) => {
        const active = r.subscriptions[0]
        setSubscribedProductID(active?.product_id ?? null)
        setSubscribedPlatform(active?.platform ?? null)
        setSubscribedExpiresAt(active?.expires_at ?? null)
      })
      .catch(() => {
        setSubscribedProductID(null)
        setSubscribedPlatform(null)
        setSubscribedExpiresAt(null)
      })
  }, [connectionStatus, isRegistered, organizationID, hasActiveOffer, setSubscribedProductID, setSubscribedPlatform, setSubscribedExpiresAt])

  const setTempAgentDefaultsStore = useAppStore((s) => s.setTempAgentDefaultsStore)
  useEffect(() => {
    if (connectionStatus !== 'connected' || !isRegistered || !organizationID) {
      setTempAgentDefaultsStore(null)
      return
    }
    fetchTempAgentDefaults(organizationID)
      .then((r) => {
        if (!r.error) {
          setTempAgentDefaultsStore({
            host_id: r.defaults.host_id ?? null,
            harness_id: r.defaults.harness_id ?? null,
            ai_brain_model_id: r.defaults.ai_brain_model_id ?? null,
            harness_name: r.defaults.harness_name ?? null,
          })
        }
      })
      .catch(() => { /* non-fatal; CreateEntitySheet gates on null */ })
  }, [connectionStatus, isRegistered, organizationID, setTempAgentDefaultsStore])

  // iOS WKWebView scrolls the document on both axes when a text field
  // is focused to lift it above the keyboard, and doesn't scroll back
  // on blur. The residual scroll made every position:fixed element —
  // the nav-stack screens with their back buttons — render offset after
  // a keyboard cycle. global.css now locks document scroll to prevent
  // that drift; this effect is a belt-and-suspenders reset for any
  // quirk (autocomplete bar dismissal, orientation change, etc.) that
  // still manages to slip past the CSS lock.
  useEffect(() => {
    function snapToOrigin() {
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0)
      }
    }
    function onFocusOut(e: FocusEvent) {
      const target = e.target as HTMLElement | null
      if (!target) return
      const tag = target.tagName
      if (tag !== 'INPUT' && tag !== 'TEXTAREA') return
      setTimeout(snapToOrigin, 0)
    }
    document.addEventListener('focusout', onFocusOut, true)
    // WKWebView scrolls the *document* to lift a focused field above the
    // keyboard, and the instant it does is racy relative to the visualViewport
    // resize: sometimes the scroll lands AFTER our resize-driven reset, leaving
    // the whole screen shoved up past the status bar until blur (the "input
    // pane flies off the top of the screen" bug — intermittent because it's a
    // timing race). The app has no legitimate document scroll (body is
    // overflow:hidden; every list scrolls in its own container), so any nonzero
    // document offset is that artifact. Pin it back to 0 the moment it appears,
    // whatever the event ordering — this is what actually closes the race.
    window.addEventListener('scroll', snapToOrigin, { passive: true })
    window.visualViewport?.addEventListener('resize', snapToOrigin)
    return () => {
      document.removeEventListener('focusout', onFocusOut, true)
      window.removeEventListener('scroll', snapToOrigin)
      window.visualViewport?.removeEventListener('resize', snapToOrigin)
    }
  }, [])

  return (
    <div className="app">
      {!isRegistered ? (
        <RegistrationScreen />
      ) : isResolvingOrg ? (
        <div className="app__resolving" />
      ) : isOrphaned ? (
        <OrphanedScreen />
      ) : (
        <>
          <div className="app__tab-content">
            <div className={`app__tab${activeTab === 'home' ? ' app__tab--active' : ''}`}>
              <HomeScreen />
            </div>
            <div className={`app__tab${activeTab === 'household' ? ' app__tab--active' : ''}`}>
              <HouseholdScreen />
            </div>
            <div className={`app__tab${activeTab === 'account' ? ' app__tab--active' : ''}`}>
              <AccountScreen />
            </div>
          </div>
          <BottomTabBar active={activeTab} onChange={setActiveTab} />
        </>
      )}
      <NavStack />
      <PermissionOverlay />
      <AnnouncementOverlay />
      <SheetStack />
      <Toasts />
      <InitiatorNotices />
      <OutdatedOverlay />
    </div>
  )
}
